import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, LayersControl, useMap, useMapEvents } from 'react-leaflet';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';
import debounce from 'lodash/debounce';

import api from '~/services/api';
import toast from '~/services/toast';
import { API_NODE_URL, APP_SETTINGS } from '~/constants';
import {
  LAT_MIN_BRASIL,
  LAT_MAX_BRASIL,
  LNG_MIN_BRASIL,
  LNG_MAX_BRASIL,
  LAT_INICIAL_FALLBACK,
  LNG_INICIAL_FALLBACK
} from './geoLocalizacao.constants';

import './leaflet-setup';

const coordenadaValida = valor =>
  valor !== null && valor !== undefined && String(valor).trim() !== '' && !Number.isNaN(Number(valor));

const coordenadasValidasNoBrasil = (lat, lng) =>
  coordenadaValida(lat) &&
  coordenadaValida(lng) &&
  Number(lat) >= LAT_MIN_BRASIL &&
  Number(lat) <= LAT_MAX_BRASIL &&
  Number(lng) >= LNG_MIN_BRASIL &&
  Number(lng) <= LNG_MAX_BRASIL;

const montarEnderecoCompleto = endereco => {
  if (!endereco) return '';

  const partes = [
    [endereco?.logradouro, endereco?.logradouroNumero].filter(Boolean).join(', '),
    endereco?.bairro,
    [endereco?.intCidade?.cidade, endereco?.intCidade?.uf].filter(Boolean).join(' - ')
  ].filter(Boolean);

  return partes.join(' - ');
};

const MapClickHandler = ({ onSelect }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onSelect(lat, lng);
    }
  });

  return null;
};

const MapFlyTo = ({ position, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.flyTo(position, zoom, { animate: true, duration: 1.5 });
    }
  }, [position]);

  return null;
};

const ModalMapaEndereco = ({
  showModal,
  onClose,
  endereco,
  latitude,
  longitude,
  localizacaoAtual,
  onConfirm
}) => {
  const temaAtual = JSON.parse(sessionStorage.getItem(APP_SETTINGS));
  const { usuario } = useSelector(state => state.auth ?? {});

  const temCoordsIniciais = coordenadasValidasNoBrasil(latitude, longitude);
  const temLocalizacaoAtual =
    !temCoordsIniciais &&
    coordenadaValida(localizacaoAtual?.lat) &&
    coordenadaValida(localizacaoAtual?.lng);

  const [posicaoInicial] = useState(() => {
    if (temCoordsIniciais) return [Number(latitude), Number(longitude)];
    if (temLocalizacaoAtual) return [Number(localizacaoAtual.lat), Number(localizacaoAtual.lng)];
    return [LAT_INICIAL_FALLBACK, LNG_INICIAL_FALLBACK];
  });
  const [zoomInicial] = useState(temCoordsIniciais ? 17 : temLocalizacaoAtual ? 15 : 14);

  const [marcador, setMarcador] = useState(temCoordsIniciais ? posicaoInicial : null);
  const [voarPara, setVoarPara] = useState(null);

  const [inputBusca, setInputBusca] = useState('');
  const [opcoes, setOpcoes] = useState([]);
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [loadingBusca, setLoadingBusca] = useState(false);
  const [loadingGeocodificacao, setLoadingGeocodificacao] = useState(false);

  const enderecoCompleto = useMemo(() => montarEnderecoCompleto(endereco), [endereco]);
  const disparouBuscaInicial = useRef(false);

  const buscarEnderecos = useCallback(
    debounce(async valor => {
      if (!valor || valor.trim().length < 3) {
        setOpcoes([]);
        return;
      }

      setLoadingBusca(true);
      try {
        const { data } = await api.post(`${API_NODE_URL}/consulta/buscar-endereco`, {
          endereco: valor
        });

        setOpcoes(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length === 0) {
          toast.info(`Não encontrado nenhum lugar correspondente à "${valor}"`);
        }
      } catch (error) {
        toast.error('Não foi possível pesquisar o endereço informado.');
      } finally {
        setLoadingBusca(false);
      }
    }, 600),
    []
  );

  useEffect(() => {
    if (!temCoordsIniciais && enderecoCompleto && !disparouBuscaInicial.current) {
      disparouBuscaInicial.current = true;
      setInputBusca(enderecoCompleto);
      setDropdownAberto(true);
      buscarEnderecos(enderecoCompleto);
    }
  }, [enderecoCompleto, temCoordsIniciais, buscarEnderecos]);

  const handleSelecionarEndereco = async opcao => {
    if (!opcao?.placeId) return;

    setLoadingGeocodificacao(true);
    try {
      const { data } = await api.get(
        `${API_NODE_URL}/consulta/geocodificacao/${opcao.placeId}`
      );

      if (data?.lat && data?.lng) {
        const posicao = [data.lat, data.lng];
        setMarcador(posicao);
        setVoarPara(posicao);
      }
    } catch (error) {
      toast.error('Não foi possível geocodificar o endereço selecionado.');
    } finally {
      setLoadingGeocodificacao(false);
    }
  };

  const handleClickMapa = (lat, lng) => {
    setMarcador([lat, lng]);
  };

  const handleConfirmar = () => {
    if (!marcador) return;

    onConfirm({
      geoLatitude: marcador[0],
      geoLongitude: marcador[1],
      geoOrigem: 'cadastro_cliente_pdv',
      geoDataHora: new Date(),
      geoUsuarioId: usuario?.id ?? null
    });
  };

  return (
    <Mui.Dialog open={showModal} onClose={onClose} PaperComponent={Mui.Paper} maxWidth="lg" fullWidth>
      <Mui.DialogTitle>
        <Mui.AppBar color="primary" position="static">
          <Mui.Toolbar>
            <Mui.Box style={{ minWidth: 180 }}>
              <Mui.Typography variant="h5" style={{ textTransform: 'uppercase' }}>
                Localizar no Mapa
              </Mui.Typography>
            </Mui.Box>
            <Mui.Box flexGrow={1} />
            <Mui.IconButton color="inherit" onClick={onClose} aria-label="close">
              <MuiIcons.Close />
            </Mui.IconButton>
          </Mui.Toolbar>
        </Mui.AppBar>
      </Mui.DialogTitle>

      <Mui.DialogContent style={{ padding: 16 }}>
        <Mui.Grid container spacing={2}>
          <Mui.Grid item xs={12}>
            <Autocomplete
              freeSolo
              openOnFocus
              open={dropdownAberto}
              onOpen={() => setDropdownAberto(true)}
              onClose={() => setDropdownAberto(false)}
              options={opcoes}
              getOptionLabel={option => (typeof option === 'string' ? option : option?.endereco ?? '')}
              filterOptions={options => options}
              inputValue={inputBusca}
              loading={loadingBusca}
              loadingText="Pesquisando..."
              noOptionsText="Nenhum resultado encontrado"
              onInputChange={(_, value, reason) => {
                setInputBusca(value);
                if (reason === 'input') buscarEnderecos(value);
              }}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') handleSelecionarEndereco(value);
              }}
              renderInput={params => (
                <Mui.TextField
                  {...params}
                  autoFocus={!temCoordsIniciais && Boolean(enderecoCompleto)}
                  label="Pesquisar endereço"
                  placeholder="ex: Av. Getulio Vargas"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                    style: {
                      color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF",
                      fontWeight: 'bold',
                    }
                  }}
                  inputProps={{
                    ...params.inputProps,
                    autocomplete: 'randomStringToTrickBrowsersThatIgnoreOff',
                    style: {
                      color: temaAtual?.theme === "DEFAULT" ? '#000' : "#FFF"
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingBusca || loadingGeocodificacao ? (
                          <Mui.CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Mui.Grid>

          <Mui.Grid item xs={12}>
            <div style={{ height: 480, width: '100%' }}>
              <MapContainer
                center={posicaoInicial}
                zoom={zoomInicial}
                style={{ height: '100%', width: '100%' }}
              >
                <LayersControl position="bottomleft">
                  <LayersControl.BaseLayer checked name="Padrão">
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
                      subdomains="abc"
                      maxZoom={19}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Satélite">
                    <TileLayer
                      attribution="Tiles &copy; Esri"
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      maxZoom={18}
                    />
                  </LayersControl.BaseLayer>
                </LayersControl>

                <MapClickHandler onSelect={handleClickMapa} />
                {voarPara && <MapFlyTo position={voarPara} zoom={17} />}
                {marcador && <Marker position={marcador} />}
              </MapContainer>
            </div>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogContent>

      <Mui.Divider />
      <Mui.DialogActions>
        <Mui.Box display="flex" justifyContent="flex-end" p={1}>
          <Mui.Button
            variant="contained"
            color="primary"
            size="large"
            disabled={!marcador}
            onClick={handleConfirmar}
            startIcon={<MuiIcons.CheckCircle />}
          >
            Confirmar
          </Mui.Button>
          <Mui.Button size="large" onClick={onClose} style={{ marginLeft: 16 }}>
            Cancelar
          </Mui.Button>
        </Mui.Box>
      </Mui.DialogActions>
    </Mui.Dialog>
  );
};

export default ModalMapaEndereco;
